import { getJSON, postJSON } from 'common/services/api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { InvoicingRateCard, RateOverride } from '../types/invoicingRates'
import {
  INVOICING_RATE_CARDS_QUERY_KEY,
  INVOICING_RATE_CARDS_UPDATE_QUERY_KEY,
  INVOICING_RATE_CARDS_URL,
  INVOICING_RATE_HISTORY_QUERY_KEY,
  INVOICING_SETTINGS_QUERY_KEY,
  INVOICING_SETTINGS_UPDATE_QUERY_KEY,
  INVOICING_SETTINGS_URL
} from './queryConstants'

export interface InvoicingRateCardDto {
  _id?: string
  name?: string
  baseRate?: string
  currency?: string
  unit?: string
  overrides?: Omit<RateOverride, never>[]
  effectiveDate?: string
  deleted?: boolean
}

export interface UpsertInvoicingRateCardsDto {
  cards: InvoicingRateCardDto[]
}

export interface InvoicingSettings {
  enabled: boolean
}

export const useGetInvoicingRateCards = (enabled = true) => {
  return useQuery<InvoicingRateCard[]>(
    [INVOICING_RATE_CARDS_QUERY_KEY],
    async () => {
      const response = await getJSON({
        url: INVOICING_RATE_CARDS_URL,
        isRota: true
      })
      return response
    },
    { enabled, staleTime: 30_000, refetchOnWindowFocus: false }
  )
}

export const useGetInvoicingRateHistory = (
  initialId: string,
  enabled = true
) => {
  return useQuery<InvoicingRateCard[]>(
    [INVOICING_RATE_HISTORY_QUERY_KEY, initialId],
    async () => {
      const response = await getJSON({
        url: `${INVOICING_RATE_CARDS_URL}/${initialId}/history`,
        isRota: true
      })
      return response
    },
    { enabled: enabled && !!initialId }
  )
}

export const useUpdateInvoicingRateCards = () => {
  const queryClient = useQueryClient()

  const updateFunction = async (
    dto: UpsertInvoicingRateCardsDto
  ): Promise<InvoicingRateCard[]> => {
    return postJSON({
      url: INVOICING_RATE_CARDS_URL,
      method: 'PUT',
      json: dto,
      isRota: true
    })
  }

  const { mutateAsync: updateInvoicingRateCards, isLoading: isUpdating } =
    useMutation<
      InvoicingRateCard[],
      unknown,
      UpsertInvoicingRateCardsDto,
      unknown
    >([INVOICING_RATE_CARDS_UPDATE_QUERY_KEY], updateFunction, {
      onSuccess: () => {
        queryClient.invalidateQueries([INVOICING_RATE_CARDS_QUERY_KEY])
      }
    })

  return { updateInvoicingRateCards, isUpdating }
}

export const useGetInvoicingSettings = () => {
  return useQuery<InvoicingSettings>(
    [INVOICING_SETTINGS_QUERY_KEY],
    async () => {
      const response = await getJSON({
        url: INVOICING_SETTINGS_URL,
        isRota: true
      })
      return response ?? { enabled: false }
    },
    { staleTime: 30_000, refetchOnWindowFocus: false }
  )
}

export const useUpdateInvoicingSettings = () => {
  const queryClient = useQueryClient()

  const updateFunction = async (
    dto: InvoicingSettings
  ): Promise<InvoicingSettings> => {
    return postJSON({
      url: INVOICING_SETTINGS_URL,
      method: 'PUT',
      json: dto,
      isRota: true
    })
  }

  const { mutateAsync: updateInvoicingSettings, isLoading: isUpdating } =
    useMutation<InvoicingSettings, unknown, InvoicingSettings, unknown>(
      [INVOICING_SETTINGS_UPDATE_QUERY_KEY],
      updateFunction,
      {
        onSuccess: () => {
          queryClient.invalidateQueries([INVOICING_SETTINGS_QUERY_KEY])
        }
      }
    )

  return { updateInvoicingSettings, isUpdating }
}
